import React, { useEffect, useMemo, useState } from 'react';
import {
  IonBackButton,
  IonButtons,
  IonButton,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTitle,
  IonToolbar,
  IonSpinner,
  IonModal,
  IonSearchbar,
} from '@ionic/react';
import { useForm, Controller, useWatch, useFieldArray } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CreateProcessedMaterialDto,
  CreateManufacturingRecipeDto,
  Unit,
  IRawMaterial,
} from 'shared';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { useProcessedMaterialDetail, useProcessedMaterials } from '../hooks/useManufacturing';

type UnitString = Unit | 'kg' | 'g' | 'ml' | 'l' | 'bottle' | 'can' | 'packed';

interface IngredientForm {
  id?: string;
  rawMaterialId: string;
  quantityNeeded: number;
  displayUnit?: UnitString;
}

interface FormValues {
  name: string;
  unit: Unit;
  recipeYield: number;
  minStockLevel: number;
  costPerUnit: number;
  ingredients: IngredientForm[];
}

const getCompatibleUnits = (unit?: UnitString) => {
  if (!unit) return [];
  if (unit === Unit.GRAM || unit === Unit.KILOGRAM) {
    return [Unit.GRAM, Unit.KILOGRAM];
  }
  if (unit === Unit.MILLILITER || unit === Unit.LITER) {
    return [Unit.MILLILITER, Unit.LITER];
  }
  return [unit];
};

const convertUnit = (quantity: number, fromUnit?: UnitString, toUnit?: UnitString) => {
  if (!fromUnit || !toUnit || fromUnit === toUnit) {
    return quantity;
  }

  let baseQuantity = quantity;
  if (fromUnit === Unit.KILOGRAM) {
    baseQuantity = quantity * 1000;
  } else if (fromUnit === Unit.LITER) {
    baseQuantity = quantity * 1000;
  }

  if (toUnit === Unit.KILOGRAM) {
    return baseQuantity / 1000;
  }
  if (toUnit === Unit.LITER) {
    return baseQuantity / 1000;
  }

  return baseQuantity;
};


const ProcessedMaterialForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const history = useHistory();
  const { t } = useLanguage();
  const {
    createProcessedMaterialMutation,
    updateProcessedMaterialMutation,
  } = useProcessedMaterials();
  const detailQuery = useProcessedMaterialDetail(id);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      unit: Unit.GRAM,
      recipeYield: 0,
      minStockLevel: 0,
      costPerUnit: 0,
      ingredients: [{ rawMaterialId: '', quantityNeeded: 0, displayUnit: undefined }],
    },
  });

  const rawMaterialsQuery = useQuery({
    queryKey: ['raw-materials'],
    queryFn: async () => {
      const { data } = await apiClient.get<IRawMaterial[]>('/materials');
      return data;
    },
  });

  const rawMaterialsMap = useMemo(() => {
    const map = new Map<string, IRawMaterial>();
    rawMaterialsQuery.data?.forEach((material) => {
      map.set(material.id, material);
    });
    return map;
  }, [rawMaterialsQuery.data]);

  const { fields, append, remove } = useFieldArray<FormValues, 'ingredients'>({
    control,
    name: 'ingredients',
  });

  const ingredientsWatch = useWatch({ control, name: 'ingredients' });

  const [pickerIndex, setPickerIndex] = useState<number | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');

  useEffect(() => {
    if (!isEdit || !detailQuery.data) {
      return;
    }

    const material = detailQuery.data;
    reset({
      name: material.name,
      unit: material.unit,
      recipeYield: Number(material.recipeYield || 0),
      minStockLevel: Number(material.minStockLevel || 0),
      costPerUnit: Number(material.costPerUnit || 0),
      ingredients:
        material.manufacturingRecipes && material.manufacturingRecipes.length
          ? material.manufacturingRecipes.map((recipe) => ({
              id: recipe.id,
              rawMaterialId: recipe.rawMaterialId,
              quantityNeeded: Number(recipe.quantityNeeded || 0),
              displayUnit: recipe.rawMaterial?.unit,
            }))
          : [{ rawMaterialId: '', quantityNeeded: 0, displayUnit: undefined }],
    });
  }, [detailQuery.data, isEdit, reset]);

  const isSaving =
    isSubmitting ||
    createProcessedMaterialMutation.isPending ||
    updateProcessedMaterialMutation.isPending;

  const showLoading = isEdit && detailQuery.isLoading;
  const materialNotFound = isEdit && !detailQuery.isLoading && !detailQuery.data;

  const onSubmit = async (values: FormValues) => {
    clearErrors('ingredients');
    const validIngredients = values.ingredients.filter(
      (row) => row.rawMaterialId && Number(row.quantityNeeded) > 0,
    );

    if (validIngredients.length === 0) {
      setError('ingredients', {
        type: 'manual',
        message: t('pleaseAddIngredient') ?? 'Please add at least one ingredient with quantity',
      });
      return;
    }

    const baseDto: CreateProcessedMaterialDto = {
      name: values.name,
      unit: values.unit,
      recipeYield: Number(values.recipeYield || 0),
      minStockLevel: Number(values.minStockLevel || 0),
      costPerUnit: Number(values.costPerUnit || 0),
    };

    if (!isEdit) {
      const response = await createProcessedMaterialMutation.mutateAsync({
        ...baseDto,
        currentStock: 0,
      });
      const material = response.data;

      const recipes: CreateManufacturingRecipeDto[] = validIngredients.map((row) => {
        const rawMaterial = rawMaterialsMap.get(row.rawMaterialId);
        const baseUnit = rawMaterial?.unit;
        const quantityNumber = Number(row.quantityNeeded || 0);
        const quantity = baseUnit
          ? convertUnit(quantityNumber, row.displayUnit ?? baseUnit, baseUnit)
          : quantityNumber;

        return {
          processedMaterialId: material.id,
          rawMaterialId: row.rawMaterialId,
          quantityNeeded: quantity,
        };
      });

      if (recipes.length > 0) {
        await Promise.all(
          recipes.map((recipe) =>
            apiClient.post('/processed-materials/manufacturing-recipes', recipe),
          ),
        );
      }

      history.replace('/tabs/manufacturing');
      return;
    }

    if (!id) {
      return;
    }

    await updateProcessedMaterialMutation.mutateAsync({
      id,
      dto: baseDto,
    });

    const existingRecipes = detailQuery.data?.manufacturingRecipes ?? [];
    if (existingRecipes.length > 0) {
      await Promise.all(
        existingRecipes.map((recipe) =>
          apiClient.delete(`/processed-materials/manufacturing-recipes/${recipe.id}`),
        ),
      );
    }

    const recipes: CreateManufacturingRecipeDto[] = validIngredients.map((row) => {
      const rawMaterial = rawMaterialsMap.get(row.rawMaterialId);
      const baseUnit = rawMaterial?.unit;
      const quantityNumber = Number(row.quantityNeeded || 0);
      const quantity = baseUnit
        ? convertUnit(quantityNumber, row.displayUnit ?? baseUnit, baseUnit)
        : quantityNumber;

      return {
        processedMaterialId: id,
        rawMaterialId: row.rawMaterialId,
        quantityNeeded: quantity,
      };
    });

    if (recipes.length > 0) {
      await Promise.all(
        recipes.map((recipe) =>
          apiClient.post('/processed-materials/manufacturing-recipes', recipe),
        ),
      );
    }

    history.replace(`/tabs/manufacturing/${id}`);
  };

  const pageTitle =
    (isEdit ? t('editProcessedMaterial') : t('addProcessedMaterial')) ??
    (isEdit ? 'Edit processed material' : 'Add processed material');
  const backHref = isEdit && id ? `/tabs/manufacturing/${id}` : '/tabs/manufacturing';

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={backHref} />
          </IonButtons>
          <IonTitle>{pageTitle}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {showLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <IonSpinner />
          </div>
        ) : materialNotFound ? (
          <IonText color="medium">
            <p>{t('noData')}</p>
          </IonText>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <IonList>
              <IonItem>
                <IonLabel position="stacked">{t('name')}</IonLabel>
                <Controller
                  control={control}
                  name="name"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <IonInput
                      value={field.value}
                      onIonChange={(event) => field.onChange(event.detail.value ?? '')}
                    />
                  )}
                />
                {errors.name ? <IonText color="danger">{t('enterName') ?? 'Enter name'}</IonText> : null}
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">{t('unit')}</IonLabel>
                <Controller
                  control={control}
                  name="unit"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <IonSelect
                      value={field.value}
                      onIonChange={(event) => field.onChange(event.detail.value as Unit)}
                    >
                      <IonSelectOption value={Unit.GRAM}>g</IonSelectOption>
                      <IonSelectOption value={Unit.KILOGRAM}>kg</IonSelectOption>
                      <IonSelectOption value={Unit.MILLILITER}>ml</IonSelectOption>
                      <IonSelectOption value={Unit.LITER}>l</IonSelectOption>
                    </IonSelect>
                  )}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">{t('recipeYieldLabel')}</IonLabel>
                <Controller
                  control={control}
                  name="recipeYield"
                  rules={{ required: true, min: 0 }}
                  render={({ field }) => (
                    <IonInput
                      type="number"
                      value={field.value?.toString() ?? ''}
                      onIonChange={(event) => field.onChange(Number(event.detail.value ?? 0))}
                    />
                  )}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">{t('minStockLevel')}</IonLabel>
                <Controller
                  control={control}
                  name="minStockLevel"
                  rules={{ min: 0 }}
                  render={({ field }) => (
                    <IonInput
                      type="number"
                      value={field.value?.toString() ?? ''}
                      onIonChange={(event) => field.onChange(Number(event.detail.value ?? 0))}
                    />
                  )}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">{t('costPerUnit')}</IonLabel>
                <Controller
                  control={control}
                  name="costPerUnit"
                  rules={{ min: 0 }}
                  render={({ field }) => (
                    <IonInput
                      type="number"
                      value={field.value?.toString() ?? ''}
                      onIonChange={(event) => field.onChange(Number(event.detail.value ?? 0))}
                    />
                  )}
                />
              </IonItem>
            </IonList>

            <IonText color="primary">
              <h2 style={{ marginTop: 24 }}>{t('ingredients')}</h2>
            </IonText>

            <IonList>
              {fields.map((fieldItem, index) => {
                const watchedRow = ingredientsWatch?.[index];
                const selectedMaterialId = watchedRow?.rawMaterialId ?? fieldItem.rawMaterialId;
                const selectedMaterial = selectedMaterialId
                  ? rawMaterialsMap.get(selectedMaterialId)
                  : undefined;
                const baseUnit = selectedMaterial?.unit;
                const compatibleUnits = getCompatibleUnits(baseUnit);
                const selectedUnit = watchedRow?.displayUnit || baseUnit;

                return (
                  <IonItem key={fieldItem.id ?? `${index}-${selectedMaterialId || 'new'}`} lines="full">
                    <IonLabel position="stacked">{t('rawMaterial')}</IonLabel>
                    <Controller
                      control={control}
                      name={`ingredients.${index}.rawMaterialId`}
                      rules={{ required: true }}
                      render={({ field }) => {
                        const material = field.value ? rawMaterialsMap.get(field.value) : selectedMaterial;
                        return (
                          <IonButton
                            expand="block"
                            fill="outline"
                            type="button"
                            style={{ marginTop: 8 }}
                            onClick={() => setPickerIndex(index)}
                          >
                            {material?.name || t('selectMaterial') || 'Select material'}
                          </IonButton>
                        );
                      }}
                    />

                    <IonLabel position="stacked" style={{ marginTop: 12 }}>
                      {t('quantity')}
                    </IonLabel>
                    <Controller
                      control={control}
                      name={`ingredients.${index}.quantityNeeded`}
                      rules={{ required: true, min: 0 }}
                      render={({ field }) => (
                        <IonInput
                          type="number"
                          value={field.value?.toString() ?? ''}
                          onIonChange={(event) => field.onChange(Number(event.detail.value ?? 0))}
                        />
                      )}
                    />

                    <IonLabel position="stacked" style={{ marginTop: 12 }}>
                      {t('unit')}
                    </IonLabel>
                    <Controller
                      control={control}
                      name={`ingredients.${index}.displayUnit`}
                      render={({ field }) => (
                        <IonSelect
                          value={field.value ?? selectedUnit}
                          placeholder={t('selectUnit') ?? 'Select unit'}
                          disabled={!selectedMaterialId}
                          onIonChange={(event) => {
                            const newUnit = event.detail.value as UnitString;
                            const currentQuantity = Number(
                              ingredientsWatch?.[index]?.quantityNeeded || 0,
                            );
                            const previousUnit = field.value || baseUnit || newUnit;
                            const converted = convertUnit(currentQuantity, previousUnit, newUnit);
                            setValue(`ingredients.${index}.quantityNeeded`, converted, {
                              shouldDirty: true,
                            });
                            field.onChange(newUnit);
                          }}
                        >
                          {compatibleUnits.map((unitOption) => (
                            <IonSelectOption key={unitOption} value={unitOption}>
                              {unitOption}
                            </IonSelectOption>
                          ))}
                        </IonSelect>
                      )}
                    />

                    <IonButtons slot="end" style={{ marginTop: 12 }}>
                      <IonButton
                        type="button"
                        color="danger"
                        onClick={() => remove(index)}
                        disabled={fields.length === 1}
                      >
                        {t('removeRow') ?? 'Remove'}
                      </IonButton>
                    </IonButtons>
                  </IonItem>
                );
              })}
            </IonList>

            <IonButton
              expand="block"
              type="button"
              onClick={() => append({ rawMaterialId: '', quantityNeeded: 0, displayUnit: undefined })}
              style={{ marginTop: 12 }}
            >
              {t('addIngredients')}
            </IonButton>

            <IonModal isOpen={pickerIndex !== null} onDidDismiss={() => setPickerIndex(null)}>
              <IonHeader>
                <IonToolbar>
                  <IonTitle>{t('selectMaterial') ?? 'Select material'}</IonTitle>
                  <IonButtons slot="end">
                    <IonButton onClick={() => setPickerIndex(null)}>{t('close')}</IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonHeader>
              <IonContent className="ion-padding">
                <IonSearchbar
                  value={pickerSearch}
                  onIonChange={(event) => setPickerSearch(event.detail.value ?? '')}
                  placeholder={t('search') ?? 'Search'}
                />
                <IonList>
                  {(rawMaterialsQuery.data ?? [])
                    .filter((material) =>
                      material.name.toLowerCase().includes(pickerSearch.toLowerCase()),
                    )
                    .map((material) => (
                      <IonItem
                        key={material.id}
                        button
                        detail
                        onClick={() => {
                          if (pickerIndex === null) return;
                          setValue(`ingredients.${pickerIndex}.rawMaterialId`, material.id, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          setValue(`ingredients.${pickerIndex}.displayUnit`, material.unit, {
                            shouldDirty: true,
                          });
                          setValue(`ingredients.${pickerIndex}.quantityNeeded`, 0, {
                            shouldDirty: true,
                          });
                          clearErrors('ingredients');
                          setPickerIndex(null);
                        }}
                      >
                        <IonLabel>
                          <h3>{material.name}</h3>
                          <p>{material.unit}</p>
                        </IonLabel>
                      </IonItem>
                    ))}
                </IonList>
              </IonContent>
            </IonModal>
            {errors.ingredients?.message ? (
              <IonText color="danger">
                <p>{errors.ingredients.message}</p>
              </IonText>
            ) : null}

            <IonButton
              expand="block"
              type="submit"
              disabled={isSaving}
              style={{ marginTop: 24 }}
            >
              {isSaving ? t('saving') ?? 'Saving…' : t('save')}
            </IonButton>
          </form>
        )}
      </IonContent>
    </IonPage>
  );
};

export default ProcessedMaterialForm;
